import { useCallback, useContext } from 'react';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { GrowthbookReadyContext } from 'services/growthbook/GrowthbookWrapper';
import { getProviderConfig } from 'config';

/**
 * Returns a predicate telling whether a provider's linkout offers may
 * redirect the user to the provider's ordering site. Three postures, checked
 * in this order:
 *
 * 1. `linkoutOffersEnabled: false` — never links out, Growthbook is not
 *    consulted. For integrated providers whose linkout url belongs to another
 *    surface. Checked first so it doubles as a kill switch for a provider that
 *    also carries a flag.
 * 2. `linkoutOffersFeatureFlag` — links out only when the flag affirmatively
 *    evaluates to true; an unset flag, a still-loading client, or a Growthbook
 *    outage all fail closed so clicks fall back to the normal checkout flow.
 * 3. Neither set — always links out, Growthbook is never consulted.
 *
 * The provider code is lowercased before lookup because it reaches us from
 * s12y while `getProviderConfig` matches case-sensitively; without this a
 * casing mismatch would miss the config entirely and fall through to (3),
 * silently linking out for a provider we meant to deny.
 */
export function useLinkoutOffersEnabled(): (providerCode: string) => boolean {
  const growthbook = useGrowthBook();
  const isGrowthbookReady = useContext(GrowthbookReadyContext);

  return useCallback(
    (providerCode: string) => {
      const providerConfig = getProviderConfig({
        providerCode: providerCode?.toLowerCase() ?? '',
      });

      // `=== false` and not falsiness: an absent flag means enabled, so an
      // unconfigured provider has to fall through rather than be denied.
      if (providerConfig?.linkoutOffersEnabled === false) return false;

      const flagName = providerConfig?.linkoutOffersFeatureFlag;

      if (!flagName) return true;
      if (!isGrowthbookReady || !growthbook) return false;

      return growthbook.isOn(flagName);
    },
    [growthbook, isGrowthbookReady]
  );
}
